import { Box, Flex, HStack, Icon, Link, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiArrowRight, FiCheck, FiCircle, FiShield, FiX } from "react-icons/fi";
import type { IconType } from "react-icons";
import { Trans } from "react-i18next";

export interface QualificationReviewItem {
  name: string;
  verdict: boolean | null;
}

interface Props {
  items: QualificationReviewItem[];
  title?: string;
  icon?: IconType;
  onSeeAll?: () => void;
}

const segmentBg = (verdict: boolean | null) => {
  if (verdict === true) return "smSuccess";
  if (verdict === false) return "smError";
  return "smBackgroundTertiary";
};

const itemColor = (verdict: boolean | null) => {
  if (verdict === true) return "smSuccess";
  if (verdict === false) return "smError";
  return "smBackgroundTertiary";
};

export const QualificationsReviewProgressCard = ({ items, title, icon, onSeeAll }: Props) => {
  const { t } = useTranslation();
  const heading = title ?? t("Qualification requirements");
  const HeadingIcon = icon ?? FiShield;
  const total = items.length;
  const fulfilled = items.filter((i) => i.verdict === true).length;
  const notFulfilled = items.filter((i) => i.verdict === false).length;
  const pending = total - fulfilled - notFulfilled;
  const allReviewed = pending === 0 && total > 0;

  return (
    <Box bg="smBackground" border="1px solid" borderColor="smBorder" rounded="xl" px="4" py="4">
      <HStack spacing="2" mb="3" align="center">
        <Icon as={HeadingIcon} color="smPrimary" w="3.5" h="3.5" />
        <Text
          fontSize="xs"
          fontWeight="bold"
          color="smMuted"
          textTransform="uppercase"
          letterSpacing="wider"
        >
          {heading}
        </Text>
      </HStack>

      <Text fontSize="sm" color="smText" mb="2.5">
        {allReviewed ? (
          fulfilled === total ? (
            <Trans
              i18nKey="All {{total}} requirements are fulfilled"
              values={{ total }}
              components={{ 1: <Box as="strong" fontWeight="bold" /> }}
            />
          ) : (
            <Trans
              i18nKey="{{count}} of {{total}} fulfilled"
              values={{ count: fulfilled, total }}
              components={{ 1: <Box as="strong" fontWeight="bold" /> }}
            />
          )
        ) : (
          <Trans
            i18nKey="{{count}} of {{total}} reviewed"
            values={{ count: fulfilled + notFulfilled, total }}
            components={{ 1: <Box as="strong" fontWeight="bold" /> }}
          />
        )}
      </Text>

      <Flex h="2" gap="0.5" mb="3" rounded="full" overflow="hidden">
        {items.map((item, i) => (
          <Box key={i} flex="1" bg={segmentBg(item.verdict)} />
        ))}
      </Flex>

      <Stack spacing="1.5" mb="3">
        {items.map((item, i) => (
          <HStack key={i} spacing="2" align="center">
            <Icon
              as={item.verdict === true ? FiCheck : item.verdict === false ? FiX : FiCircle}
              color={itemColor(item.verdict)}
              w="3.5"
              h="3.5"
              flexShrink="0"
            />
            <Text
              fontSize="sm"
              color={item.verdict === null ? "smMuted" : "smText"}
              noOfLines={1}
            >
              {item.name}
            </Text>
          </HStack>
        ))}
      </Stack>

      {onSeeAll && (
        <Link
          onClick={onSeeAll}
          color="smPrimary"
          fontSize="sm"
          fontWeight="medium"
          display="inline-flex"
          alignItems="center"
          gap="1.5"
        >
          {t("See all requirements")}
          <Icon as={FiArrowRight} />
        </Link>
      )}
    </Box>
  );
};
